import React from "react";

// Generated from public/calculators/value-chain.html (markup only). Inputs are
// controlled via `v`/`on`; outputs come from `o` (see valueChainMath.js).
export default function ValueChainMarkup({ v, on, o, channel, setChannel, tips, productRows, addProduct, exportCsv, fire, ro }) {
  return (
    <>



<div className="header">
  <div className="header-inner">
    <div>
      <div className="header-badge">CPG Financial Tool</div>
      
      <p className="header-sub">Model the full financial picture of any retail, distributor, or wholesale opportunity — from invoice price to net profit — before you commit.</p>
    </div>
    <div className="header-stats">
      <div className="header-stat">
        <span className="val" id="stat-gm">{o["stat-gm"]}</span>
        <span className="lbl">Gross Margin</span>
      </div>
      <div className="header-stat">
        <span className="val" id="stat-np">{o["stat-np"]}</span>
        <span className="lbl">Net Profit %</span>
      </div>
      <div className="header-stat">
        <span className="val" id="stat-units">{o["stat-units"]}</span>
        <span className="lbl">Annual Units</span>
      </div>
    </div>
  </div>
</div>

<div className="main">
  
  <div className="left-col">

    
    <div className="section-card">
      <div className="section-header">
        <div className="section-icon icon-amber">🏪</div>
        <div>
          <div className="section-title">Opportunity Setup</div>
          <div className="section-desc">Define the channel type and basic opportunity details. The channel you're selling into fundamentally changes the margin expectations, fee structures, and payment terms — retail chains carry the highest overhead, while direct wholesale offers the cleanest margin.</div>
        </div>
      </div>
      <div className="section-body">
        <div className="field form-full" style={{ marginBottom: '16px' }}>
          <div className="field-label">
            Sales Channel
            <span className="tooltip-icon">?<span className="tooltip-text">Choose how you're distributing this product. Retail = selling to a chain store buyer directly. Distributor = selling through a distribution partner who resells to retailers. Wholesale = selling in bulk to a single buyer or co-op. Each has different margin expectations and fee structures.</span></span>
          </div>
          <div className="channel-toggle">
            <button className={`channel-btn${channel === 'retail' ? ' active' : ''}`} onClick={() => setChannel('retail')} disabled={ro} data-testid="vc-channel-retail">
              <span className="channel-icon">🏬</span>Retail
            </button>
            <button className={`channel-btn${channel === 'distributor' ? ' active' : ''}`} onClick={() => setChannel('distributor')} disabled={ro} data-testid="vc-channel-distributor">
              <span className="channel-icon">🚚</span>Distributor
            </button>
            <button className={`channel-btn${channel === 'wholesale' ? ' active' : ''}`} onClick={() => setChannel('wholesale')} disabled={ro} data-testid="vc-channel-wholesale">
              <span className="channel-icon">📦</span>Wholesale
            </button>
            <button className={`channel-btn${channel === 'ecommerce' ? ' active' : ''}`} onClick={() => setChannel('ecommerce')} disabled={ro} data-testid="vc-channel-ecommerce">
              <span className="channel-icon">🌐</span>E-Commerce
            </button>
          </div>
        </div>
        <div className="form-grid">
          <div className="field">
            <div className="field-label">
              Customer / Buyer Name
              <span className="tooltip-icon">?<span className="tooltip-text">The name of the retailer, distributor, or buyer you're modeling this opportunity for. This is for your reference only — it won't affect any calculations.</span></span>
            </div>
            <input type="text" id="customerName" value={v["customerName"]} onChange={on} disabled={ro} data-testid="vc-customerName" placeholder="e.g. Kroger, UNFI, Costco" />
          </div>
          <div className="field">
            <div className="field-label">
              Win Probability
              <span className="tooltip-icon">?<span className="tooltip-text">How confident are you that this deal will close? Used to calculate the probability-weighted revenue in the summary. 100% = certain, 50% = coin flip. Be honest — this helps prioritize where to focus your time.</span></span>
            </div>
            <div className="range-field">
              <div className="range-row">
                <input type="range" min="0" max="100" step="5" id="probability" value={v["probability"]} onChange={on} disabled={ro} data-testid="vc-probability" />
                <span className="range-val" id="probVal">{v.probability + "%"}</span>
              </div>
            </div>
          </div>
          <div className="field">
            <div className="field-label">
              Expected Start Date
              <span className="tooltip-icon">?<span className="tooltip-text">When do you expect to start shipping to this customer? Used for pipeline planning. Pipeline fill (initial stocking order) typically arrives before your first recurring replenishment shipment.</span></span>
            </div>
            <input type="text" id="startDate" value={v["startDate"]} onChange={on} disabled={ro} data-testid="vc-startDate" placeholder="e.g. Q3 2025 / Oct 2025" />
          </div>
          <div className="field">
            <div className="field-label">
              Payment Terms (Days)
              <span className="tooltip-icon">?<span className="tooltip-text">Net payment terms this customer requires. Net 30 = they pay 30 days after invoice. Longer terms tie up your working capital and can significantly impact your effective margin. Standard retail is Net 30–60; some large retailers demand Net 90+.</span></span>
            </div>
            <div className="input-suffix">
              <span>days</span>
              <input type="number" id="paymentTerms" value={v["paymentTerms"]} onChange={on} disabled={ro} data-testid="vc-paymentTerms" min="0" max="120" />
            </div>
          </div>
        </div>
      </div>
    </div>

    
    <div className="section-card">
      <div className="section-header">
        <div className="section-icon icon-teal">💲</div>
        <div>
          <div className="section-title">Products & Pricing</div>
          <div className="section-desc">Enter each SKU you're proposing for this account. Your <strong>invoice (selling) price</strong> is what you charge the buyer — not the retail shelf price. COGS is your all-in landed cost to produce and deliver one unit to the customer's warehouse. The gap between these two numbers is your gross margin starting point.</div>
        </div>
      </div>
      <div className="section-body">
        <div className="info-box">
          💡 Invoice price = what you bill the buyer. COGS = your total cost (supplier price + packaging + freight-in). Enter one row per SKU or product line.
        </div>
        <br />
        <table className="products-table" id="productTable">
          <thead>
            <tr>
              <th style={{ width: '32%' }}>
                SKU / Item Name
                <span className="tooltip-icon" style={{ fontStyle: 'normal' }}>?<span className="tooltip-text">The name or SKU code for this product as it appears on your price sheet or in your systems.</span></span>
              </th>
              <th style={{ width: '16%' }}>
                Invoice Price
                <span className="tooltip-icon" style={{ fontStyle: 'normal' }}>?<span className="tooltip-text">The price per unit you charge the buyer on your invoice. Also called "sell-in price" or "wholesale price." This is NOT the retail shelf price.</span></span>
              </th>
              <th style={{ width: '14%' }}>
                COGS / Unit
                <span className="tooltip-icon" style={{ fontStyle: 'normal' }}>?<span className="tooltip-text">Cost of Goods Sold per unit: your supplier cost + any royalties built into cost + packaging + freight to warehouse. This is your floor — everything else comes out of the spread between this and your invoice price.</span></span>
              </th>
              <th style={{ width: '14%' }}>
                Royalty %
                <span className="tooltip-icon" style={{ fontStyle: 'normal' }}>?<span className="tooltip-text">If you pay a royalty on sales of this SKU (e.g., a licensed brand), enter the percentage here. Applied to invoice price before other deductions. Leave 0 if no royalty applies.</span></span>
              </th>
              <th style={{ width: '16%' }}>
                Units / Year
                <span className="tooltip-icon" style={{ fontStyle: 'normal' }}>?<span className="tooltip-text">Estimated annual unit sales for this SKU across all of this customer's stores. Can be calculated in the Volume section below.</span></span>
              </th>
              <th style={{ width: '8%' }}></th>
            </tr>
          </thead>
          <tbody id="productRows">{productRows}</tbody>
        </table>
        <button className="add-product-btn" onClick={() => addProduct()} disabled={ro} data-testid="vc-add-product">
          <span style={{ fontSize: '18px', lineHeight: '1' }}>+</span> Add SKU / Product
        </button>
      </div>
    </div>

    
    <div className="section-card">
      <div className="section-header">
        <div className="section-icon icon-navy">📊</div>
        <div>
          <div className="section-title">Volume Projection</div>
          <div className="section-desc">Build your annual unit forecast from store-level velocity. Start with how many stores will carry your product, then estimate weekly sales per store. This bottoms-up approach keeps you honest — a national rollout at low velocity can still be a bad deal. Also account for your pipeline fill (the initial stocking order before recurring velocity kicks in).</div>
        </div>
      </div>
      <div className="section-body">
        <div className="form-grid">
          <div className="field">
            <div className="field-label">
              Number of Stores
              <span className="tooltip-icon">?<span className="tooltip-text">Total number of doors (store locations) that will carry your product. For a test launch, this may be a small subset. For a national retailer, this could be 1,000–4,500 stores. Ask the buyer for their expected door count before assuming full chain.</span></span>
            </div>
            <input type="number" id="numStores" value={v["numStores"]} onChange={on} disabled={ro} data-testid="vc-numStores" min="0" />
          </div>
          <div className="field">
            <div className="field-label">
              Units / Store / Week
              <span className="tooltip-icon">?<span className="tooltip-text">Your estimated average weekly sell-through per store location. Impulse / checkout items might do 1–3 units/store/week. Everyday staples could do 5–15. Be conservative — buyers set replenishment thresholds around velocity. Low velocity = pulled from shelf.</span></span>
            </div>
            <input type="number" id="unitsPerStoreWeek" value={v["unitsPerStoreWeek"]} onChange={on} disabled={ro} data-testid="vc-unitsPerStoreWeek" step="0.5" min="0" />
          </div>
          <div className="field">
            <div className="field-label">
              Active Weeks / Year
              <span className="tooltip-icon">?<span className="tooltip-text">How many weeks per year will product be actively selling at this account? 52 = full year. If you're starting mid-year or seasonal, adjust down. Most first-year programs run 26–40 weeks before reset evaluations.</span></span>
            </div>
            <input type="number" id="activeWeeks" value={v["activeWeeks"]} onChange={on} disabled={ro} data-testid="vc-activeWeeks" min="1" max="52" />
          </div>
          <div className="field">
            <div className="field-label">
              Pipeline Fill Units
              <span className="tooltip-icon">?<span className="tooltip-text">The initial stocking order to fill shelves before regular replenishment begins. Rule of thumb: (stores × facings × shelf depth) or roughly 2–4 weeks of projected velocity. This shows up as revenue upfront but should not inflate your recurring run-rate.</span></span>
            </div>
            <input type="number" id="pipelineFill" value={v["pipelineFill"]} onChange={on} disabled={ro} data-testid="vc-pipelineFill" min="0" />
          </div>
        </div>
        <div className="section-divider"><span>Calculated Volume</span></div>
        <div className="form-grid-4">
          <div className="metric-item">
            <div className="m-lbl">Weekly Units</div>
            <div className="m-val" id="calc-weekly">{o["calc-weekly"]}</div>
          </div>
          <div className="metric-item">
            <div className="m-lbl">Recurring Units</div>
            <div className="m-val" id="calc-recurring">{o["calc-recurring"]}</div>
          </div>
          <div className="metric-item">
            <div className="m-lbl">Pipeline Fill</div>
            <div className="m-val" id="calc-pipeline">{o["calc-pipeline"]}</div>
          </div>
          <div className="metric-item">
            <div className="m-lbl">Total Units / Yr</div>
            <div className="m-val" id="calc-total">{o["calc-total"]}</div>
          </div>
        </div>
        <div className="section-divider"><span>Apply volume to products</span></div>
        <div className="info-box">The total annual units above are used in the financial model. If you have multiple SKUs, you can distribute volume manually by editing the "Units / Year" column in the product table above.</div>
      </div>
    </div>

    
    <div className="section-card">
      <div className="section-header">
        <div className="section-icon icon-red">📉</div>
        <div>
          <div className="section-title">Value Chain Deductions</div>
          <div className="section-desc">These are the percentage-based costs that flow through your value chain on every dollar of sales. They're often overlooked until a deal is already signed. Understanding each layer — distribution, broker commissions, retailer deductions, and payment terms discounts — is the core of professional CPG financial modeling.</div>
        </div>
      </div>
      <div className="section-body">

        <div className="sub-label">Cost of Sales</div>
        <div className="form-grid">
          <div className="field">
            <div className="field-label">
              Distribution % of Sales
              <span className="tooltip-icon">?<span className="tooltip-text">The percentage of your invoice sales retained by your distributor as their margin, or what you pay in outbound freight and handling. DSD distributors typically take 25–35%. Broadline (UNFI, KeHE) typically 18–25%. Direct store delivery with your own trucks may be lower. If selling direct to retailer, enter your freight cost as a % of sales.</span></span>
            </div>
            <div className="input-suffix">
              <span>%</span>
              <input type="number" id="distPct" value={v["distPct"]} onChange={on} disabled={ro} data-testid="vc-distPct" min="0" max="100" step="0.5" />
            </div>
          </div>
          <div className="field">
            <div className="field-label">
              Broker / Sales Rep %
              <span className="tooltip-icon">?<span className="tooltip-text">Commission paid to your food broker or sales representative for managing and growing this account. Standard broker commission is 3–5% of net sales (after distributor margin). Some brokers charge flat fees or hybrid structures — convert to a % equivalent here.</span></span>
            </div>
            <div className="input-suffix">
              <span>%</span>
              <input type="number" id="brokerPct" value={v["brokerPct"]} onChange={on} disabled={ro} data-testid="vc-brokerPct" min="0" max="25" step="0.5" />
            </div>
          </div>
          <div className="field">
            <div className="field-label">
              Royalty % of Sales
              <span className="tooltip-icon">?<span className="tooltip-text">If you have a licensed brand, celebrity endorsement, or IP that requires a royalty payment calculated as a percentage of sales (vs. a fixed per-unit cost), enter it here. This is separate from the per-unit royalty in the product table. Common for licensed/co-branded products: 5–15% of net sales.</span></span>
            </div>
            <div className="input-suffix">
              <span>%</span>
              <input type="number" id="royaltyPct" value={v["royaltyPct"]} onChange={on} disabled={ro} data-testid="vc-royaltyPct" min="0" max="50" step="0.5" />
            </div>
          </div>
          <div className="field">
            <div className="field-label">
              Misc. Deductions %
              <span className="tooltip-icon">?<span className="tooltip-text">Retailer or distributor deductions that appear on remittances: short pays, price discrepancies, returns/reclamation, unsaleables, and administrative fees. Budget 1–3% as a baseline — large retailers (Walmart, Kroger) may run 2–5%. These come off the top of what you actually receive.</span></span>
            </div>
            <div className="input-suffix">
              <span>%</span>
              <input type="number" id="miscPct" value={v["miscPct"]} onChange={on} disabled={ro} data-testid="vc-miscPct" min="0" max="20" step="0.5" />
            </div>
          </div>
        </div>

        <div className="section-divider"><span>Trade Spend</span></div>
        <div className="form-grid">
          <div className="field">
            <div className="field-label">
              Promotions % of Sales
              <span className="tooltip-icon">?<span className="tooltip-text">Your total promotional investment as a % of sales — includes temporary price reductions (TPRs), scan-backs, off-invoice allowances, and BOGO deals. Retailers budget these at 10–20%+ for promotional-driven categories. Every promoted deal reduces your effective realized price.</span></span>
            </div>
            <div className="input-suffix">
              <span>%</span>
              <input type="number" id="promoPct" value={v["promoPct"]} onChange={on} disabled={ro} data-testid="vc-promoPct" min="0" max="40" step="0.5" />
            </div>
          </div>
          <div className="field">
            <div className="field-label">
              Payment Terms Discount %
              <span className="tooltip-icon">?<span className="tooltip-text">Some retailers take a cash discount for early payment (e.g., 2/10 Net 30 = 2% discount if paid within 10 days). Enter that discount rate here. Even small percentages compound significantly at scale — 2% on $1M of sales = $20K off your receivables.</span></span>
            </div>
            <div className="input-suffix">
              <span>%</span>
              <input type="number" id="termsPct" value={v["termsPct"]} onChange={on} disabled={ro} data-testid="vc-termsPct" min="0" max="10" step="0.25" />
            </div>
          </div>
        </div>

        <div id="channel-tips" style={{ marginTop: '14px' }} dangerouslySetInnerHTML={{ __html: tips }} />
      </div>
    </div>

    
    <div className="section-card">
      <div className="section-header">
        <div className="section-icon icon-amber">🏷️</div>
        <div>
          <div className="section-title">Fixed & One-Time Costs</div>
          <div className="section-desc">These costs happen regardless of volume and must be recovered from the margin this account generates. Slotting fees are paid upfront just to get on-shelf; trade shows build the relationship that made the deal possible. Spread these across the year to understand how long it takes to break even on the account.</div>
        </div>
      </div>
      <div className="section-body">
        <div className="form-grid">
          <div className="field">
            <div className="field-label">
              Slotting Fees
              <span className="tooltip-icon">?<span className="tooltip-text">One-time or per-reset fee charged by the retailer for shelf space allocation. Can range from $0 (natural channel, Costco) to $25K–$500K+ for major conventional chains with large store counts. Always negotiate — some retailers waive slotting for strong velocity projections or new item fees instead.</span></span>
            </div>
            <div className="input-prefix">
              <span>$</span>
              <input type="number" id="slotting" value={v["slotting"]} onChange={on} disabled={ro} data-testid="vc-slotting" min="0" />
            </div>
          </div>
          <div className="field">
            <div className="field-label">
              Promotional / Display Fees
              <span className="tooltip-icon">?<span className="tooltip-text">Fixed fees for promotional programs, ad features, end-cap displays, or digital promotions (e.g., circular, app placement). These are separate from scan-back promotions — these are fees charged to participate regardless of lift. Budget $5K–$50K/year per account for mid-tier retailers.</span></span>
            </div>
            <div className="input-prefix">
              <span>$</span>
              <input type="number" id="promoFees" value={v["promoFees"]} onChange={on} disabled={ro} data-testid="vc-promoFees" min="0" />
            </div>
          </div>
          <div className="field">
            <div className="field-label">
              Annual Account Fees
              <span className="tooltip-icon">?<span className="tooltip-text">Ongoing administrative or program fees: EDI compliance fees, warehouse handling charges, sustainability fees, shelf reset fees, or annual vendor program fees. Read your vendor agreement carefully — these add up. Common at club, drug, and mass channels.</span></span>
            </div>
            <div className="input-prefix">
              <span>$</span>
              <input type="number" id="annualFees" value={v["annualFees"]} onChange={on} disabled={ro} data-testid="vc-annualFees" min="0" />
            </div>
          </div>
          <div className="field">
            <div className="field-label">
              Trade Shows / Travel
              <span className="tooltip-icon">?<span className="tooltip-text">Attributed cost of trade shows (booth, travel, samples, materials) and sales travel for this account. For a national account, this might be $5K–$25K/year once you allocate fairly across accounts. Smaller specialty buyers may require regular in-person visits.</span></span>
            </div>
            <div className="input-prefix">
              <span>$</span>
              <input type="number" id="tradeshows" value={v["tradeshows"]} onChange={on} disabled={ro} data-testid="vc-tradeshows" min="0" />
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

  
  <div className="results-panel">

    
    <div className="results-card">
      <div className="results-header">
        <h3>Value Chain P&L</h3>
        <p id="opp-name-display">{o["opp-name-display"]}</p>
      </div>
      <div className="waterfall" id="waterfallDisplay">
        <div className="wf-row">
          <span className="wf-label"><span className="dot"></span>Total Invoice Sales</span>
          <span className="wf-val" id="wf-sales">{o["wf-sales"]}</span>
        </div>
        <div className="wf-divider"></div>
        <div className="wf-row wf-deduct">
          <span className="wf-label"><span className="dot"></span>Less: COGS</span>
          <span className="wf-val" id="wf-cogs">{o["wf-cogs"]}</span>
        </div>
        <div className="wf-row wf-total wf-gross">
          <span className="wf-label">Gross Margin</span>
          <span className="wf-val" id="wf-gm">{o["wf-gm"]}</span>
        </div>
        <div className="wf-row wf-revenue">
          <span className="wf-label" style={{ paddingLeft: '8px' }}>Gross Margin %</span>
          <span className="wf-val" id="wf-gm-pct">{o["wf-gm-pct"]}</span>
        </div>
        <div className="wf-divider"></div>
        <div className="wf-row wf-deduct">
          <span className="wf-label"><span className="dot"></span>Less: Distribution</span>
          <span className="wf-val" id="wf-dist">{o["wf-dist"]}</span>
        </div>
        <div className="wf-row wf-deduct">
          <span className="wf-label"><span className="dot"></span>Less: Royalty</span>
          <span className="wf-val" id="wf-royalty">{o["wf-royalty"]}</span>
        </div>
        <div className="wf-row wf-deduct">
          <span className="wf-label"><span className="dot"></span>Less: Broker</span>
          <span className="wf-val" id="wf-broker">{o["wf-broker"]}</span>
        </div>
        <div className="wf-row wf-deduct">
          <span className="wf-label"><span className="dot"></span>Less: Deductions</span>
          <span className="wf-val" id="wf-misc">{o["wf-misc"]}</span>
        </div>
        <div className="wf-row wf-deduct">
          <span className="wf-label"><span className="dot"></span>Less: Promotions</span>
          <span className="wf-val" id="wf-promo">{o["wf-promo"]}</span>
        </div>
        <div className="wf-row wf-deduct">
          <span className="wf-label"><span className="dot"></span>Less: Terms Discount</span>
          <span className="wf-val" id="wf-terms">{o["wf-terms"]}</span>
        </div>
        <div className="wf-divider"></div>
        <div className="wf-row wf-total">
          <span className="wf-label">Net Revenue</span>
          <span className="wf-val" id="wf-netr">{o["wf-netr"]}</span>
        </div>
        <div className="wf-divider"></div>
        <div className="wf-row wf-deduct">
          <span className="wf-label"><span className="dot"></span>Less: Fixed Costs</span>
          <span className="wf-val" id="wf-fixed">{o["wf-fixed"]}</span>
        </div>
      </div>

      <div className="net-profit-display">
        <div className="net-label">Net Profit</div>
        <div className={o.cls["netProfitDisplay"]} id="netProfitDisplay">{o["netProfitDisplay"]}</div>
        <div>
          <span className={o.cls["netPctDisplay"]} id="netPctDisplay">{o["netPctDisplay"]}</span>
        </div>
      </div>
    </div>

    
    <div className="summary-metrics">
      <h4>Key Metrics</h4>
      <div className="metric-grid">
        <div className="metric-item">
          <div className="m-lbl">Gross Margin %</div>
          <div className={o.cls["m-gm"]} id="m-gm">{o["m-gm"]}</div>
        </div>
        <div className="metric-item">
          <div className="m-lbl">Cost of Sales %</div>
          <div className="m-val" id="m-cos">{o["m-cos"]}</div>
        </div>
        <div className="metric-item">
          <div className="m-lbl">Weighted Revenue</div>
          <div className="m-val" id="m-weighted">{o["m-weighted"]}</div>
        </div>
        <div className="metric-item">
          <div className="m-lbl">Break-Even Units</div>
          <div className="m-val" id="m-breakeven">{o["m-breakeven"]}</div>
        </div>
        <div className="metric-item">
          <div className="m-lbl">Realized Price / Unit</div>
          <div className="m-val" id="m-realized">{o["m-realized"]}</div>
        </div>
        <div className="metric-item">
          <div className="m-lbl">Net Profit / Unit</div>
          <div className={o.cls["m-npunit"]} id="m-npunit">{o["m-npunit"]}</div>
        </div>
      </div>
    </div>

    
    <button className="export-btn" onClick={exportCsv} data-testid="value-chain-export-csv">
      ⬇ Export Summary to CSV
    </button>

    
    <div className="cpg-save-row" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
      <button type="button"
              id="cpg-save-draft"
              className="cpg-save-btn"
              data-testid="iframe-save-draft"
              data-cpg-save-mode="draft"
              onClick={() => fire('save-draft')} disabled={ro}>
        Save Draft to Projects
      </button>
      <button type="button"
              id="cpg-save-result"
              className="cpg-save-btn"
              data-testid="iframe-save-result"
              data-cpg-save-mode="result"
              onClick={() => fire('save-result')} disabled={ro}>
        Save Result to Projects
      </button>
    </div>

  </div>
</div>


    </>
  );
}
